import { useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { QueryClientContext, useQueryClient } from '@tanstack/react-query';
import type { Attachment, NextcloudStatus } from '@/api';
import { useApi } from '@/api/provider';
import { NextcloudFolderPicker } from '@/app/lazyUi';
import { Icon } from '@/components/Icon';
import { useNextcloudStatus } from '@/features/nextcloud/nextcloudStatus';
import { SETTINGS_PATH } from '@/features/nextcloud/settingsPath';
import { filesFor, getSaves, startSave, subscribeSaves, type SaveFile } from '@/features/nextcloud/save/saveStore';
import '@/features/nextcloud/save/save.css';
import {
  ObjectUrlRegistry,
  subresourceFailureCopy,
  triggerDownload,
  type SubresourceFetcher,
} from '@/features/message-frame/subresources';
import { formatBytes } from '@/utils/format';
import { MAX_TEXT_PREVIEW_BYTES, planPreview, readPreviewText, typeLabel } from './preview';
import './attachments.css';

/*
 * # The attachment list (WP-37, #172)
 *
 * The chip strip this replaces could do one thing: download. A list row says
 * what the part is, how big it is, and offers a preview when this app can
 * show the bytes *itself* — never when showing them would mean handing a
 * browser a document made of a stranger's bytes. `./preview.ts` holds that
 * decision and the argument for it, per media type.
 *
 * Everything here obeys the rules the rest of the reading pane already does:
 *
 * - every byte is fetched by the page with a session proof (ADR 0041) through
 *   `fetchSubresource`, never by an element pointing at `/api/…`;
 * - every URL is minted by `ObjectUrlRegistry`, which re-types anything that
 *   is not a raster image to `application/octet-stream` — the defence from
 *   #267, where a `blob:` SVG opened as a page ran script on this origin;
 * - no element in this subtree can become a browsing context. There is no
 *   `<iframe>`, `<embed>` or `<object>` (the app-shell CSP refuses all three
 *   anyway, ADR 0049), no React escape hatch for raw markup, and no anchor to
 *   anything of the sender's: a text preview is a React text child of a
 *   `<pre>`, and an image preview is an `<img>`, which is a decode and nothing
 *   more. (The one link is ours: Settings → Nextcloud, when a link broke.)
 * - *Save to Nextcloud* (ADR 0086) moves no byte through the page: the server
 *   reads the part from the mailbox and writes it to Nextcloud itself.
 * - no inner scroller (ADR 0048). A preview grows in flow, bounded by
 *   `max-height` on the image and by a line cap on the text, so the document
 *   stays the one scroller on a phone.
 */

export interface AttachmentListProps {
  /** Resets every preview when it changes; also the registry's lifetime. */
  messageId: string;
  attachments: readonly Attachment[];
  /**
   * Parts the HTML body already draws (`cid:` inline images). Listing them
   * beside the real attachments is duplicate chrome, so they sit behind a
   * disclosure instead of being dropped — a reader looking for "the picture
   * in this mail" should be able to save it.
   */
  inlineAttachments?: readonly Attachment[];
  fetchSubresource: SubresourceFetcher;
  /**
   * A short-lived URL on the user-content origin for one attachment (ADR
   * 0078). When present, Download goes there instead of through a blob: URL of
   * this origin: the bytes never become an object of the app origin at all,
   * and a large file streams to disk instead of through page memory.
   */
  downloadLink?: ((attachment: Attachment) => Promise<string>) | undefined;
  /**
   * *Save to Nextcloud* (ADR 0086): the mailbox the message is in and what it
   * is called, for the save and for "Files shared from mail" if a link is
   * made. Absent: no Nextcloud controls at all. Present, they still appear
   * only with a linked Nextcloud -- a deployment without the feature, or a
   * person who never linked one, sees exactly the list as before.
   */
  nextcloudSave?: NextcloudSaveTarget | undefined;
  /** Opens the folder picker as a full-screen sheet rather than a dialog. */
  handheld?: boolean;
  /**
   * Opens an attached email in the app (ADR 0092). Present: every row the
   * server marked as an attached email, with a path to open it by, gets a
   * View action; `from` is the button, for focus to return to.
   */
  onViewAttachedMessage?: ((attachment: Attachment, from: HTMLElement) => void) | undefined;
}

export interface NextcloudSaveTarget {
  accountId: string;
  subject: string;
  /** The Message-ID header, so a message moved meanwhile is found again. */
  rfcMessageId?: string | undefined;
  /**
   * The message the parts are read through, when the list is not that
   * message's own: an attached email's list (ADR 0092) saves parts of the
   * email at `attachedPath` inside `messageId`.
   */
  messageId?: string | undefined;
  attachedPath?: string | undefined;
}

/** Why a once-linked Nextcloud is no longer, in the list's words. */
const WITHDRAWN: Record<string, string> = {
  rejected: 'Nextcloud stopped accepting the link',
  password_changed: 'the link was removed when your password changed',
  signed_out_everywhere: 'the link was removed when you signed out everywhere',
  host_changed: 'the link was replaced',
};

/** A row's word on its save, beside the buttons; undefined when there is none. */
function saveStateText(file: SaveFile | undefined): string | undefined {
  switch (file?.state) {
    case 'queued':
      return 'Waiting…';
    case 'reading':
      return 'Reading…';
    case 'uploading': {
      const percent = file.totalBytes > 0 ? Math.min(100, Math.floor((file.doneBytes / file.totalBytes) * 100)) : 0;
      return `Saving ${percent}%`;
    }
    case 'saved':
      return file.renamed === true && file.name !== undefined ? `Saved as ${file.name}` : 'Saved to Nextcloud';
    default:
      return undefined;
  }
}

/**
 * Starts a download from the user-content origin.
 *
 * A transient anchor, clicked: the browser navigates to the URL, the response
 * is `Content-Disposition: attachment`, and a navigation answered with an
 * attachment becomes a download that leaves this page where it is. No
 * `download` attribute, because a browser ignores it cross-origin; the header
 * is what decides. `noopener noreferrer` so nothing of ours reaches that
 * origin, not even a Referer.
 */
function navigateToDownload(url: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noopener noreferrer';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

type PreviewState =
  | { status: 'loading' }
  | { status: 'image'; url: string; width?: number; height?: number }
  | { status: 'text'; text: string; truncated: boolean };

/**
 * Everything this list remembers, tagged with the message it is about.
 *
 * The house idiom (`ReadingPane`): state carries its message id rather than
 * being reset by an effect, so opening the next message starts clean by
 * construction — one render, no stale frame, nothing to forget to clear.
 */
interface ListState {
  messageId: string;
  previews: Record<string, PreviewState>;
  showInline: boolean;
  busyId?: string | undefined;
  failure?: string | undefined;
}

const emptyState = (messageId: string): ListState => ({
  messageId,
  previews: {},
  showInline: false,
});

/** What the list needs to draw the Save controls; undefined draws none. */
interface NextcloudControls {
  status: NextcloudStatus | undefined;
  files: ReadonlyMap<string, SaveFile> | undefined;
  openPicker: (files: readonly Attachment[], from: HTMLElement | null) => void;
}

export function AttachmentList(props: AttachmentListProps) {
  // The Save controls need the query cache; a tree without one (a component
  // test, an embedder) gets the list exactly as it was before them.
  const client = useContext(QueryClientContext);
  if (props.nextcloudSave !== undefined && client !== undefined) {
    return <AttachmentListWithNextcloud {...props} target={props.nextcloudSave} />;
  }
  return <AttachmentListView {...props} nc={undefined} />;
}

/**
 * The list with *Save to Nextcloud* (ADR 0086). The status is the composer's
 * query, shared and cached, and only asked for when there is something to
 * save. The picker is lazy; the save itself goes to the store, which outlives
 * this list.
 */
function AttachmentListWithNextcloud(props: AttachmentListProps & { target: NextcloudSaveTarget }) {
  const { target, messageId, attachments, inlineAttachments = [], handheld = false } = props;
  const nc = useNextcloudStatus(attachments.length + inlineAttachments.length > 0);
  const saves = useSyncExternalStore(subscribeSaves, getSaves, getSaves);
  const saveMessageId = target.messageId ?? messageId;
  const files = saves.length > 0 ? filesFor(target.accountId, saveMessageId, target.attachedPath) : undefined;
  const [picking, setPicking] = useState<{ files: readonly Attachment[]; from: HTMLElement | null }>();
  const api = useApi();
  const queryClient = useQueryClient();

  const save = (folder: string) => {
    if (picking === undefined) return;
    startSave(
      { api, queryClient },
      {
        accountId: target.accountId,
        messageId: saveMessageId,
        subject: target.subject,
        ...(target.rfcMessageId !== undefined ? { rfcMessageId: target.rfcMessageId } : {}),
        ...(target.attachedPath !== undefined ? { attachedPath: target.attachedPath } : {}),
        folder,
        files: picking.files.map((a) => ({ partId: a.id, filename: a.filename, sizeBytes: a.sizeBytes })),
      },
    );
    setPicking(undefined);
  };

  return (
    <>
      <AttachmentListView
        {...props}
        nc={{ status: nc.status, files, openPicker: (list, from) => setPicking({ files: list, from }) }}
      />
      {picking !== undefined && nc.status !== undefined && (
        <SavePicker
          status={nc.status}
          handheld={handheld}
          files={picking.files}
          from={picking.from}
          onClose={() => setPicking(undefined)}
          onChoose={save}
        />
      )}
    </>
  );
}

function AttachmentListView({
  messageId,
  attachments,
  inlineAttachments = [],
  fetchSubresource,
  downloadLink,
  onViewAttachedMessage,
  nc,
}: AttachmentListProps & { nc: NextcloudControls | undefined }) {
  const [stored, setStored] = useState<ListState>(() => emptyState(messageId));
  const state = stored.messageId === messageId ? stored : emptyState(messageId);
  const { busyId, failure, previews, showInline } = state;

  /** Applies a change to *this* message's state, whatever was stored before. */
  const update = useCallback(
    (change: (current: ListState) => ListState): void => {
      setStored((current) =>
        change(current.messageId === messageId ? current : emptyState(messageId)),
      );
    },
    [messageId],
  );

  /*
   * The object URLs this list minted, and the only thing that frees them. A
   * previewed image is held in page memory for as long as its `blob:` URL
   * exists, so the whole registry goes when the message changes or the pane
   * unmounts — the same lifetime the download path has always had.
   */
  const registryRef = useRef<ObjectUrlRegistry | null>(null);
  useEffect(
    () => () => {
      registryRef.current?.revokeAll();
      registryRef.current = null;
    },
    [messageId],
  );

  const mint = useCallback((blob: Blob): string => {
    registryRef.current ??= new ObjectUrlRegistry();
    return registryRef.current.create(blob);
  }, []);

  const setPreview = useCallback(
    (id: string, preview: PreviewState | undefined): void => {
      update((current) => {
        const previews = { ...current.previews };
        if (preview === undefined) delete previews[id];
        else previews[id] = preview;
        return { ...current, previews };
      });
    },
    [update],
  );

  const download = useCallback(
    async (attachment: Attachment): Promise<void> => {
      update((current) => ({ ...current, busyId: attachment.id, failure: undefined }));
      try {
        if (downloadLink) {
          navigateToDownload(await downloadLink(attachment));
          update((current) => ({ ...current, busyId: undefined }));
          return;
        }
        const blob = await fetchSubresource(attachment.downloadUrl);
        // The filename is the one the backend already sanitized for its own
        // Content-Disposition; nothing here re-derives it.
        triggerDownload(mint(blob), attachment.filename);
        update((current) => ({ ...current, busyId: undefined }));
      } catch (cause) {
        const failure = subresourceFailureCopy(cause);
        update((current) => ({ ...current, busyId: undefined, failure }));
      }
    },
    [downloadLink, fetchSubresource, mint, update],
  );

  const togglePreview = useCallback(
    async (attachment: Attachment, kind: 'image' | 'text'): Promise<void> => {
      if (previews[attachment.id]) {
        // Closing does not revoke: the reader may open it again, and a
        // reopened preview should not cost a second fetch. The registry is
        // freed as a whole when the message changes.
        setPreview(attachment.id, undefined);
        return;
      }
      update((current) => ({
        ...current,
        failure: undefined,
        previews: { ...current.previews, [attachment.id]: { status: 'loading' } },
      }));
      try {
        const blob = await fetchSubresource(attachment.downloadUrl);
        setPreview(
          attachment.id,
          kind === 'image'
            ? { status: 'image', url: mint(blob) }
            : { status: 'text', ...(await readPreviewText(blob)) },
        );
      } catch (cause) {
        const failure = subresourceFailureCopy(cause);
        update((current) => {
          const previews = { ...current.previews };
          delete previews[attachment.id];
          return { ...current, previews, failure };
        });
      }
    },
    [fetchSubresource, mint, previews, setPreview, update],
  );

  /** The image's real size, once the browser has actually decoded it. */
  const measure = useCallback(
    (id: string, image: HTMLImageElement): void => {
      const { naturalWidth: width, naturalHeight: height } = image;
      if (width === 0 || height === 0) return;
      update((current) => {
        const preview = current.previews[id];
        if (preview?.status !== 'image') return current;
        return { ...current, previews: { ...current.previews, [id]: { ...preview, width, height } } };
      });
    },
    [update],
  );

  const inlineCount = inlineAttachments.length;
  const rows = showInline ? [...attachments, ...inlineAttachments] : attachments;

  /*
   * Save to Nextcloud (ADR 0086): only with a linked Nextcloud. Inline images
   * are savable one by one once their disclosure is open, and are never part
   * of Save all: they are the pictures in the body, not the files the sender
   * attached.
   */
  const ncStatus = nc?.status;
  const linked = ncStatus?.state === 'active';
  const withdrawn = ncStatus?.state === 'withdrawn';
  const saveFiles = nc?.files;
  const inRouter = useInRouterContext();

  if (rows.length === 0 && inlineCount === 0) return null;

  const openPicker = (files: readonly Attachment[], from: HTMLElement | null) => nc?.openPicker(files, from);
  const busy = (id: string) => {
    const state = saveFiles?.get(id)?.state;
    return state === 'queued' || state === 'reading' || state === 'uploading';
  };

  return (
    <div className="attachment-list">
      {rows.length > 0 && (
        <ul className="attachment-list__rows" aria-label="Attachments">
          {rows.map((attachment) => {
            const plan = planPreview(attachment.contentType);
            const preview = previews[attachment.id];
            const open = preview !== undefined;
            // An attached email opens in the app instead of being previewed
            // (ADR 0092); the note about previews does not apply to it.
            const viewable =
              onViewAttachedMessage !== undefined &&
              attachment.attachedMessage === true &&
              attachment.attachedPath !== undefined;
            return (
              <li className="attachment-row" key={attachment.id} aria-label={attachment.filename}>
                <div className="attachment-row__line">
                  <Icon name="attachment" size={16} />
                  <span className="attachment-row__name truncate">{attachment.filename}</span>
                  <span className="attachment-row__meta">
                    {attachment.attachedMessage === true ? 'Email' : typeLabel(attachment.contentType)} ·{' '}
                    {formatBytes(attachment.sizeBytes)}
                  </span>
                  <span className="spacer" />
                  {viewable && (
                    <button
                      type="button"
                      className="btn btn--ghost attachment-row__action attachment-row__view"
                      aria-label={`View email ${attachment.filename}`}
                      aria-haspopup="dialog"
                      onClick={(event) => onViewAttachedMessage(attachment, event.currentTarget)}
                    >
                      View
                    </button>
                  )}
                  {!viewable && plan.kind !== 'none' && (
                    <button
                      type="button"
                      className="btn btn--ghost attachment-row__action"
                      aria-expanded={open}
                      aria-label={
                        open ? `Hide preview of ${attachment.filename}` : `Preview ${attachment.filename}`
                      }
                      onClick={() => void togglePreview(attachment, plan.kind as 'image' | 'text')}
                    >
                      {open ? 'Hide preview' : 'Preview'}
                    </button>
                  )}
                  {/*
                    A button, not a link: `<a download href>` is a browser-issued
                    GET carrying only cookies, which ADR 0041 no longer accepts.
                  */}
                  <button
                    type="button"
                    className="btn btn--ghost attachment-row__action"
                    disabled={busyId === attachment.id}
                    aria-label={`Download ${attachment.filename}`}
                    onClick={() => void download(attachment)}
                  >
                    {busyId === attachment.id ? 'Downloading…' : 'Download'}
                  </button>
                  {linked && saveStateText(saveFiles?.get(attachment.id)) !== undefined && (
                    <span className="attachment-row__save-state" data-state={saveFiles?.get(attachment.id)?.state}>
                      {saveStateText(saveFiles?.get(attachment.id))}
                    </span>
                  )}
                  {linked && (
                    <button
                      type="button"
                      className="btn btn--ghost attachment-row__action attachment-row__save"
                      disabled={busy(attachment.id)}
                      aria-label={`Save to Nextcloud: ${attachment.filename}`}
                      title="Save to Nextcloud"
                      onClick={(event) => openPicker([attachment], event.currentTarget)}
                    >
                      <Icon name="cloud" size={16} />
                      <span className="attachment-row__save-label">Save to Nextcloud</span>
                    </button>
                  )}
                </div>

                {linked && saveFiles?.get(attachment.id)?.state === 'failed' && (
                  <p className="attachment-row__problem">{saveFiles.get(attachment.id)?.problem}</p>
                )}

                {plan.kind === 'none' && !viewable && <p className="attachment-row__note">{plan.reason}</p>}

                {preview?.status === 'loading' && (
                  <p className="attachment-row__note" role="status">
                    Loading preview…
                  </p>
                )}

                {preview?.status === 'image' && (
                  <div className="attachment-preview">
                    <img
                      className="attachment-preview__image"
                      src={preview.url}
                      alt={attachment.filename}
                      onLoad={(event) => measure(attachment.id, event.currentTarget)}
                    />
                    <p className="attachment-row__note">
                      {preview.width !== undefined && preview.height !== undefined
                        ? `${preview.width} × ${preview.height} · ${formatBytes(attachment.sizeBytes)}`
                        : formatBytes(attachment.sizeBytes)}
                    </p>
                  </div>
                )}

                {preview?.status === 'text' && (
                  <div className="attachment-preview">
                    {/*
                      The sender's bytes as a React text child: escaped by the
                      renderer, never parsed as markup, and no object URL was
                      minted to show them.
                    */}
                    <pre className="attachment-preview__text">{preview.text}</pre>
                    {preview.truncated && (
                      <p className="attachment-row__note">
                        Showing the first {formatBytes(MAX_TEXT_PREVIEW_BYTES)}. Download the file for
                        the rest.
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {inlineCount > 0 && (
        <button
          type="button"
          className="btn btn--ghost attachment-list__disclosure"
          aria-expanded={showInline}
          onClick={() => update((current) => ({ ...current, showInline: !current.showInline }))}
        >
          {showInline ? 'Hide' : 'Show'} {inlineCount} inline{' '}
          {inlineCount === 1 ? 'image' : 'images'}
        </button>
      )}

      {linked && attachments.length >= 2 && (
        <div className="attachment-list__foot">
          <button
            type="button"
            className="btn attachment-list__disclosure"
            aria-label={`Save all ${attachments.length} attachments to Nextcloud`}
            disabled={attachments.some((a) => busy(a.id))}
            onClick={(event) => openPicker(attachments, event.currentTarget)}
          >
            <Icon name="cloud" size={16} /> Save all to Nextcloud
          </button>
        </div>
      )}

      {withdrawn && (
        <p className="attachment-list__nc-note">
          Save to Nextcloud is off: {WITHDRAWN[ncStatus.reason ?? ''] ?? 'the link was removed'}.{' '}
          {inRouter ? <Link to={SETTINGS_PATH}>Link it again in Settings</Link> : <a href={SETTINGS_PATH}>Link it again in Settings</a>}
        </p>
      )}

      {failure !== undefined && (
        <p className="attachment-list__error" role="alert">
          Couldn’t download that attachment. {failure}
        </p>
      )}
    </div>
  );
}

/** The lazily loaded folder picker, told what it is saving. */
function SavePicker({
  status,
  handheld,
  files,
  from,
  onClose,
  onChoose,
}: {
  status: NextcloudStatus;
  handheld: boolean;
  files: readonly Attachment[];
  from: HTMLElement | null;
  onClose: () => void;
  onChoose: (folder: string) => void;
}) {
  const what = files.length === 1 ? (files[0]?.filename ?? 'the attachment') : `${files.length} attachments`;
  return (
    <NextcloudFolderPicker
      status={status}
      handheld={handheld}
      what={what}
      count={files.length}
      onClose={onClose}
      onChoose={onChoose}
      returnFocusTo={from}
    />
  );
}
